// src/pages/Invoices.js
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Edit2, Trash2, RefreshCw, DollarSign, CheckCircle, Clock, AlertTriangle, Eye, X, Save, Download, CreditCard, Search } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CFG = {
  draft:     { label: 'Draft',     color: 'var(--text-secondary)', bg: 'var(--bg-hover)' },
  sent:      { label: 'Trimisă',   color: 'var(--accent)',         bg: '#4f8ef720' },
  paid:      { label: 'Plătită',   color: 'var(--success)',        bg: '#22c55e20' },
  partial:   { label: 'Parțial',   color: 'var(--warning)',        bg: '#f59e0b20' },
  overdue:   { label: 'Restantă',  color: 'var(--danger)',         bg: '#ef444420' },
  cancelled: { label: 'Anulată',   color: 'var(--text-secondary)', bg: 'var(--bg-hover)' },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, color: c.color, background: c.bg }}>{c.label}</span>;
}

function InvoiceModal({ invoice, clients, onClose, onSaved }) {
  const isEdit = !!invoice?.id;
  const empty = { client_id: '', issue_date: new Date().toISOString().split('T')[0], due_date: '', currency: 'RON', tax_rate: 19, discount: 0, notes: '', status: 'draft' };
  const [f, setF] = useState(isEdit ? { ...invoice } : empty);
  const [items, setItems] = useState(invoice?.items || [{ description: '', quantity: 1, unit_price: 0 }]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const addItem = () => setItems(p => [...p, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems(p => p.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const taxAmt = subtotal * ((parseFloat(f.tax_rate) || 0) / 100);
  const total = subtotal + taxAmt - (parseFloat(f.discount) || 0);

  const save = async () => {
    if (!f.client_id) return toast.error('Selectați un client');
    if (items.filter(i => i.description).length === 0) return toast.error('Adăugați cel puțin un articol');
    setLoading(true);
    try {
      const payload = { ...f, items: items.filter(i => i.description) };
      if (isEdit) { await api.put(`/invoices/${invoice.id}`, payload); toast.success('Factură actualizată'); }
      else { await api.post('/invoices', payload); toast.success('Factură creată'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 800, width: '95vw' }}>
        <div className="modal-header">
          <span className="modal-title"><FileText size={16} /> {isEdit ? `Editare ${invoice.number}` : 'Factură Nouă'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-grid form-grid-3">
            <div className="form-group"><label className="form-label">Client *</label>
              <select className="form-select" value={f.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">— Selectați —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Data Emiterii</label><input className="form-input" type="date" value={f.issue_date} onChange={e => set('issue_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Scadență</label><input className="form-input" type="date" value={f.due_date || ''} onChange={e => set('due_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Monedă</label>
              <select className="form-select" value={f.currency} onChange={e => set('currency', e.target.value)}>
                {['RON','EUR','USD','GBP'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">TVA %</label><input className="form-input" type="number" value={f.tax_rate} onChange={e => set('tax_rate', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Discount</label><input className="form-input" type="number" step="0.01" value={f.discount} onChange={e => set('discount', e.target.value)} /></div>
            {isEdit && <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>}
          </div>

          {/* Line items */}
          <div style={{ margin: '16px 0 8px', fontWeight: 700, fontSize: 13 }}>Articole</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-hover)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>DESCRIERE</th>
                <th style={{ padding: '8px 12px', width: 80, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>QTY</th>
                <th style={{ padding: '8px 12px', width: 110, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>PREȚ/UM</th>
                <th style={{ padding: '8px 12px', width: 100, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>TOTAL</th>
                <th style={{ width: 36 }} />
              </tr></thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px' }}><input className="form-input" style={{ fontSize: 13 }} value={item.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="Serviciu / Produs..." /></td>
                    <td style={{ padding: '6px 8px' }}><input className="form-input" type="number" step="0.01" style={{ fontSize: 13 }} value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} /></td>
                    <td style={{ padding: '6px 8px' }}><input className="form-input" type="number" step="0.01" style={{ fontSize: 13 }} value={item.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)} /></td>
                    <td style={{ padding: '6px 12px', fontWeight: 700, fontSize: 13 }}>{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}</td>
                    <td style={{ padding: '6px 4px' }}><button className="btn-icon" onClick={() => removeItem(i)} style={{ color: 'var(--danger)' }}><X size={13} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary" style={{ marginTop: 8, fontSize: 12 }} onClick={addItem}><Plus size={13} /> Adaugă linie</button>

          {/* Totals */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: 260 }}>
              {[['Subtotal', subtotal], [`TVA (${f.tax_rate}%)`, taxAmt], ['Discount', -(parseFloat(f.discount) || 0)]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span>{l}</span><span>{v >= 0 ? '' : '-'}{Math.abs(v).toFixed(2)} {f.currency}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 16, fontWeight: 800, borderTop: '2px solid var(--border)', marginTop: 6 }}>
                <span>TOTAL</span><span style={{ color: 'var(--accent)' }}>{total.toFixed(2)} {f.currency}</span>
              </div>
            </div>
          </div>

          <div className="form-group"><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Creare'}</>}</button>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const [f, setF] = useState({ amount: invoice.total - (invoice.paid_amount || 0), method: 'transfer', reference: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const save = async () => {
    if (!f.amount || f.amount <= 0) return toast.error('Suma trebuie să fie pozitivă');
    setLoading(true);
    try {
      await api.post(`/invoices/${invoice.id}/payments`, f);
      toast.success('Plată înregistrată');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header"><span className="modal-title"><CreditCard size={16} /> Înregistrare Plată — {invoice.number}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total factură: <strong>{invoice.total?.toFixed(2)} {invoice.currency}</strong></div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Deja plătit: <strong style={{ color: 'var(--success)' }}>{(invoice.paid_amount || 0).toFixed(2)} {invoice.currency}</strong></div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Rest de plată: <strong style={{ color: 'var(--warning)' }}>{(invoice.total - (invoice.paid_amount || 0)).toFixed(2)} {invoice.currency}</strong></div>
          </div>
          <div className="form-group"><label className="form-label">Sumă *</label><input className="form-input" type="number" step="0.01" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Metodă plată</label>
            <select className="form-select" value={f.method} onChange={e => setF(p => ({ ...p, method: e.target.value }))}>
              <option value="transfer">Transfer bancar</option><option value="card">Card</option><option value="cash">Numerar</option><option value="crypto">Crypto</option><option value="other">Altele</option>
            </select></div>
          <div className="form-group"><label className="form-label">Referință</label><input className="form-input" value={f.reference} onChange={e => setF(p => ({ ...p, reference: e.target.value }))} placeholder="Nr. ordin de plată..." /></div>
          <div className="form-group"><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : 'Înregistrează'}</button></div>
      </div>
    </div>
  );
}

export default function Invoices() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, cli, st] = await Promise.all([
        api.get('/invoices', { params: { search: search || undefined, status: filterStatus || undefined } }),
        api.get('/clients'),
        api.get('/invoices/stats'),
      ]);
      setList(inv.data); setClients(cli.data); setStats(st.data);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, [search, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const del = async (id, num) => {
    if (!window.confirm(`Ștergi factura ${num}?`)) return;
    try { await api.delete(`/invoices/${id}`); toast.success('Factură ștearsă'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Facturi & Plăți</div><div className="page-subtitle">Gestiune facturi, plăți și situație financiară</div></div>
        <button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Factură Nouă</button>
      </div>

      {stats && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Facturi', value: stats.total, color: 'var(--accent)', icon: FileText },
            { label: 'Plătite', value: stats.paid, color: 'var(--success)', icon: CheckCircle },
            { label: 'În așteptare', value: stats.pending, color: 'var(--warning)', icon: Clock },
            { label: 'Restante', value: stats.overdue, color: 'var(--danger)', icon: AlertTriangle },
            { label: 'Valoare Totală', value: `${(stats.total_amount || 0).toFixed(0)} RON`, color: 'var(--accent)', icon: DollarSign },
            { label: 'Încasată', value: `${(stats.paid_amount || 0).toFixed(0)} RON`, color: 'var(--success)', icon: CreditCard },
          ].map(k => (
            <div key={k.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${k.color}20`, marginBottom: 10 }}><k.icon size={16} color={k.color} /></div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută factură, client..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Toate statusurile</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
        </div>

        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>NR.</th><th>CLIENT</th><th>DATA</th><th>SCADENȚĂ</th><th>TOTAL</th><th>PLĂTIT</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Nicio factură</td></tr>}
                {list.map(inv => {
                  const remaining = (inv.total || 0) - (inv.paid_amount || 0);
                  return (
                    <tr key={inv.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{inv.number}</td>
                      <td>{inv.company_name || inv.contact_name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('ro-RO') : '—'}</td>
                      <td style={{ fontSize: 12, color: inv.status === 'overdue' ? 'var(--danger)' : 'inherit' }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString('ro-RO') : '—'}</td>
                      <td style={{ fontWeight: 700 }}>{(inv.total || 0).toFixed(2)} {inv.currency}</td>
                      <td style={{ fontSize: 12, color: 'var(--success)' }}>{(inv.paid_amount || 0).toFixed(2)}</td>
                      <td><StatusBadge status={inv.status} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" title="Editare" onClick={() => api.get(`/invoices/${inv.id}`).then(r => setModal(r.data))}><Edit2 size={13} /></button>
                          {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                            <button className="btn-icon" title="Înregistrează plată" style={{ color: 'var(--success)' }} onClick={() => setPayModal(inv)}><CreditCard size={13} /></button>
                          )}
                          {hasRole('superadmin', 'admin') && (
                            <button className="btn-icon" title="Ștergere" style={{ color: 'var(--danger)' }} onClick={() => del(inv.id, inv.number)}><Trash2 size={13} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <InvoiceModal invoice={modal.new ? null : modal} clients={clients} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {payModal && <PaymentModal invoice={payModal} onClose={() => setPayModal(null)} onSaved={() => { setPayModal(null); load(); }} />}
    </div>
  );
}
